import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../base';
import { pricingPlans } from '../plans/pricingPlans';
import featureFlags from '../config/featureFlags';

const isActiveStandardTrial = (userData) => {
  if (userData?.standardTrialStatus !== 'active' || !userData?.standardTrialEndsAt) {
    return false;
  }

  const rawEndsAt = userData.standardTrialEndsAt;
  const endsAt = typeof rawEndsAt?.toDate === 'function' ? rawEndsAt.toDate() : new Date(rawEndsAt);
  return endsAt.getTime() > Date.now();
};

export const useProjectLimits = (currentUser, userData) => {
  const [projectCount, setProjectCount] = useState(0);
  const [projectLimit, setProjectLimit] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [canCreateProject, setCanCreateProject] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const applyProjectLimitState = ({ nextProjectCount, nextProjectLimit, nextCanCreateProject, nextIsLoading }) => {
      if (!isMounted) {
        return;
      }

      if (typeof nextProjectCount === 'number') {
        setProjectCount(nextProjectCount);
      }
      if (typeof nextProjectLimit === 'number') {
        setProjectLimit(nextProjectLimit);
      }
      if (typeof nextCanCreateProject === 'boolean') {
        setCanCreateProject(nextCanCreateProject);
      }
      if (typeof nextIsLoading === 'boolean') {
        setIsLoading(nextIsLoading);
      }
    };

    const fetchProjectData = async () => {
      // If project limits are disabled via feature flag, allow unlimited projects
      if (!featureFlags.PROJECT_LIMITS_ENABLED) {
        applyProjectLimitState({
          nextProjectCount: 0,
          nextProjectLimit: 999,
          nextCanCreateProject: true,
          nextIsLoading: false,
        });
        return;
      }

      if (!currentUser || currentUser.email.startsWith("guest_")) {
        // Guest users have unlimited temporary projects
        applyProjectLimitState({
          nextProjectCount: 0,
          nextProjectLimit: 999,
          nextCanCreateProject: true,
          nextIsLoading: false,
        });
        return;
      }

      const currentUserRole =
        userData?.role ||
        (typeof window !== 'undefined' ? window.localStorage.getItem('userRole') : null);

      if (currentUserRole === 'admin' || currentUserRole === 'mainAdmin') {
        applyProjectLimitState({
          nextProjectCount: 0,
          nextProjectLimit: 999999,
          nextCanCreateProject: true,
          nextIsLoading: false,
        });
        return;
      }

      try {
        // Get user's current project count from Firebase
        const projectsQuery = query(
          collection(db, "Projects"),
          where("userId", "==", currentUser.uid)
        );
        const projectsSnapshot = await getDocs(projectsQuery);
        const currentProjectCount = projectsSnapshot.size;

        // Determine user's plan limit (check both planName and planType as fallback)
        let currentPlanLimit = 0;
        const userPlan = userData?.planName || userData?.planType;
        if (isActiveStandardTrial(userData)) {
          currentPlanLimit = Number(userData?.standardTrialProjectLimit || 1);
        } else if (userPlan) {
          const plan = pricingPlans.find(p => p.name === userPlan);
          if (plan) {
            currentPlanLimit = plan.projectLimit;
          }
        }

        // Add extra projects purchased (My Project purchases)
        const extraProjects = userData?.extraProjectPurchased || 0;
        const totalLimit = currentPlanLimit + extraProjects;

        applyProjectLimitState({
          nextProjectCount: currentProjectCount,
          nextProjectLimit: totalLimit,
          nextCanCreateProject: currentProjectCount < totalLimit,
        });
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching project data:', error);
        }
        // Fallback to allow creation on error
        applyProjectLimitState({ nextCanCreateProject: true });
      } finally {
        applyProjectLimitState({ nextIsLoading: false });
      }
    };

    fetchProjectData();

    return () => {
      isMounted = false;
    };
  }, [currentUser, userData]);

  return {
    projectCount,
    projectLimit,
    isLoading,
    canCreateProject,
    isAtLimit: projectCount >= projectLimit
  };
};
